import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  addVendorReview,
  createComplianceVendor,
  listComplianceVendors,
  listUsers,
  updateComplianceVendor,
  type AssuranceType,
  type DataClass,
  type ReviewOutcome,
  type VendorCriticality,
  type VendorView,
} from "@/lib/api";
import { Modal } from "@/components/Modal";

const CRITICALITY_STYLE: Record<VendorCriticality, string> = {
  critical: "bg-danger/15 text-danger",
  important: "bg-accent-muted text-accent",
  low: "border border-border text-text-secondary",
};

const ASSURANCE_LABEL: Record<AssuranceType, string> = {
  none: "None",
  soc2: "SOC 2",
  iso27001: "ISO 27001",
  both: "SOC 2 + ISO",
  other: "Other",
};

const today = () => new Date().toISOString().slice(0, 10);

export function ComplianceVendorsPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [addOpen, setAddOpen] = useState(false);
  const [showRetired, setShowRetired] = useState(false);

  const { data: vendors, isLoading } = useQuery({
    queryKey: ["compliance-vendors"],
    queryFn: listComplianceVendors,
  });

  const openId = params.get("open");
  const open = useMemo(() => vendors?.find((v) => v.vendor.id === openId) ?? null, [vendors, openId]);
  const shown = (vendors ?? []).filter((v) => showRetired || !v.retired);

  return (
    <div>
      <header className="mb-8 flex items-end justify-between gap-4">
        <div>
          <h1 className="page-title">Vendors</h1>
          <p className="mt-2 text-text-secondary">
            Everyone who holds or processes our data, tiered by how much damage their
            failure would do. Assurance expiry is tracked, not filed and forgotten.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
            />
            Show retired
          </label>
          <button
            onClick={() => setAddOpen(true)}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
          >
            Add vendor
          </button>
        </div>
      </header>

      {isLoading ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-lg" />
        </div>
      ) : shown.length === 0 ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-center">
          <div className="max-w-md">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-token-lg bg-accent-muted text-2xl text-accent">
              <i className="fa-solid fa-handshake" />
            </div>
            <h2 className="font-display text-lg font-semibold text-text-heading">
              No vendors registered
            </h2>
            <p className="mx-auto mt-1.5 text-sm text-text-secondary">
              Start with the ones holding our data: Render, Neon, Google Workspace,
              GitHub, Cloudflare, OpenAI. Each needs a tier, an owner and their current
              SOC 2 or ISO report.
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
                <th className="px-4 py-2.5 font-semibold">Vendor</th>
                <th className="px-4 py-2.5 font-semibold">Tier</th>
                <th className="px-4 py-2.5 font-semibold">Data</th>
                <th className="px-4 py-2.5 font-semibold">Owner</th>
                <th className="px-4 py-2.5 font-semibold">Assurance</th>
                <th className="px-4 py-2.5 font-semibold">Expires</th>
                <th className="px-4 py-2.5 font-semibold">Reviewed</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((v) => {
                const lapsing = v.blockers.some((b) => b.rule.startsWith("VND_ASSURANCE"));
                return (
                  <tr
                    key={v.vendor.id}
                    className="cursor-pointer border-b border-border-light hover:bg-bg-card-hover"
                    onClick={() => setParams({ open: v.vendor.id })}
                  >
                    <td className="px-4 py-2.5 font-medium text-text-heading">
                      {v.vendor.name}
                      {v.retired && <span className="ml-2 text-xs text-text-muted">retired</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${CRITICALITY_STYLE[v.vendor.criticality]}`}
                      >
                        {v.vendor.criticality}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 capitalize text-text-secondary">
                      {v.vendor.dataClass}
                      {v.vendor.dataClass === "personal" && !v.vendor.dpaOnFile && (
                        <span className="ml-1.5 text-xs text-warning">no DPA</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                      {v.vendor.owner?.name ?? <span className="text-warning">unassigned</span>}
                    </td>
                    <td className="px-4 py-2.5 text-text-secondary">
                      {ASSURANCE_LABEL[v.vendor.assurance]}
                    </td>
                    <td className={`whitespace-nowrap px-4 py-2.5 ${lapsing ? "text-warning" : "text-text-secondary"}`}>
                      {v.vendor.assuranceExpiresOn ?? "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                      {v.vendor.lastReviewedOn ?? <span className="text-warning">never</span>}
                      {v.blockers.length > 0 && (
                        <span className="ml-2 text-xs text-text-muted">{v.blockers.length}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && <VendorModal view={open} onClose={() => setParams({})} />}
      {addOpen && (
        <AddVendorModal
          onClose={() => setAddOpen(false)}
          onCreated={(id) => {
            void qc.invalidateQueries({ queryKey: ["compliance-vendors"] });
            setParams({ open: id });
          }}
        />
      )}
    </div>
  );
}

function AddVendorModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [criticality, setCriticality] = useState<VendorCriticality>("important");
  const [dataClass, setDataClass] = useState<DataClass>("internal");

  const create = useMutation({
    mutationFn: () =>
      createComplianceVendor({ name: name.trim(), purpose: purpose.trim(), criticality, dataClass }),
    onSuccess: (v) => {
      onCreated(v.vendor.id);
      onClose();
    },
  });

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
  return (
    <Modal
      open
      onClose={onClose}
      title="Add vendor"
      footer={
        <button
          onClick={() => create.mutate()}
          disabled={!name.trim() || !purpose.trim() || create.isPending}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          Add
        </button>
      }
    >
      <div className="space-y-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Neon"
          className={inputCls}
          autoFocus
        />
        <textarea
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          rows={2}
          placeholder="What we use them for — the first thing an auditor asks."
          className={inputCls}
        />
        <label className="block text-xs text-text-muted">
          Criticality
          <select
            value={criticality}
            onChange={(e) => setCriticality(e.target.value as VendorCriticality)}
            className={inputCls}
          >
            <option value="critical">Critical — holds our data, outage stops us</option>
            <option value="important">Important — meaningful but survivable</option>
            <option value="low">Low — replaceable tomorrow</option>
          </select>
        </label>
        <label className="block text-xs text-text-muted">
          Data they touch
          <select
            value={dataClass}
            onChange={(e) => setDataClass(e.target.value as DataClass)}
            className={inputCls}
          >
            <option value="none">None</option>
            <option value="internal">Internal</option>
            <option value="personal">Personal data (needs a DPA)</option>
            <option value="confidential">Confidential / client data</option>
          </select>
        </label>
        {create.isError && <p className="text-xs text-danger">{(create.error as Error).message}</p>}
      </div>
    </Modal>
  );
}

function VendorModal({ view, onClose }: { view: VendorView; onClose: () => void }) {
  const qc = useQueryClient();
  const v = view.vendor;
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: listUsers });
  const [findings, setFindings] = useState("");
  const [outcome, setOutcome] = useState<ReviewOutcome>("satisfactory");
  const [reviewedOn, setReviewedOn] = useState(today());
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["compliance-vendors"] });
    void qc.invalidateQueries({ queryKey: ["compliance-overview"] });
  };
  const fail = (e: Error) => setError(e.message);

  const patch = useMutation({
    mutationFn: (input: Parameters<typeof updateComplianceVendor>[1]) =>
      updateComplianceVendor(v.id, input),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const review = useMutation({
    mutationFn: () => addVendorReview(v.id, { findings, outcome, reviewedOn }),
    onSuccess: () => {
      setFindings("");
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";

  return (
    <Modal open onClose={onClose} title={v.name} wide>
      <div className="space-y-5 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${CRITICALITY_STYLE[v.criticality]}`}
          >
            {v.criticality}
          </span>
          <span className="text-text-muted">{v.purpose}</span>
          <label className="ml-auto flex items-center gap-2 text-text-secondary">
            Owner
            <select
              value={v.ownerId ?? ""}
              onChange={(e) => patch.mutate({ ownerId: e.target.value || null })}
              className="rounded-md border border-border bg-bg-card px-2 py-1 text-sm text-text-heading"
            >
              <option value="">— unassigned —</option>
              {users
                ?.filter((u) => u.active)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
            </select>
          </label>
        </div>

        {view.blockers.length > 0 && (
          <ul className="space-y-1">
            {view.blockers.map((b) => (
              <li key={b.rule} className="text-warning">
                <code className="mr-2 rounded bg-bg-secondary px-1.5 py-0.5 text-xs">{b.rule}</code>
                {b.message}
              </li>
            ))}
          </ul>
        )}

        <Section title="Assurance">
          <div className="grid gap-3 md:grid-cols-3">
            <label className="text-xs text-text-muted">
              Type
              <select
                value={v.assurance}
                onChange={(e) => patch.mutate({ assurance: e.target.value as AssuranceType })}
                className={inputCls}
              >
                {(Object.keys(ASSURANCE_LABEL) as AssuranceType[]).map((a) => (
                  <option key={a} value={a}>
                    {ASSURANCE_LABEL[a]}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-text-muted">
              Expires / period ends
              <input
                type="date"
                value={v.assuranceExpiresOn ?? ""}
                onChange={(e) => patch.mutate({ assuranceExpiresOn: e.target.value || null })}
                className={inputCls}
              />
            </label>
            <label className="text-xs text-text-muted">
              Review cadence (months)
              <input
                type="number"
                min={1}
                max={60}
                value={v.reviewCadenceMonths}
                onChange={(e) => patch.mutate({ reviewCadenceMonths: Number(e.target.value) })}
                className={inputCls}
              />
            </label>
          </div>
          <label className="mt-3 flex items-center gap-2 text-text-secondary">
            <input
              type="checkbox"
              checked={v.dpaOnFile}
              onChange={(e) => patch.mutate({ dpaOnFile: e.target.checked })}
            />
            Data processing agreement on file
          </label>
        </Section>

        <Section title="Sub-processors">
          <textarea
            defaultValue={v.subProcessors ?? ""}
            onBlur={(e) => patch.mutate({ subProcessors: e.target.value || null })}
            rows={2}
            placeholder="Who they in turn rely on — the list clients ask for."
            className={inputCls}
          />
        </Section>

        <Section title={`Reviews — ${view.reviews.length}`}>
          <ul className="mb-3 space-y-1.5">
            {view.reviews.map((r) => (
              <li key={r.id} className="text-text-secondary">
                <span className="font-medium text-text-heading">{r.reviewedBy?.name ?? "?"}</span> ·{" "}
                {r.reviewedOn} ·{" "}
                <span className={r.outcome === "satisfactory" ? "text-success" : "text-warning"}>
                  {r.outcome}
                </span>
                <div className="ml-4 text-xs italic text-text-muted">“{r.findings}”</div>
              </li>
            ))}
            {view.reviews.length === 0 && <li className="text-text-muted">Never reviewed.</li>}
          </ul>
          <div className="space-y-2">
            <textarea
              value={findings}
              onChange={(e) => setFindings(e.target.value)}
              rows={2}
              placeholder="What you checked and what you found — their report, incidents, changes since last time."
              className={inputCls}
            />
            <div className="flex flex-wrap gap-2">
              <select
                value={outcome}
                onChange={(e) => setOutcome(e.target.value as ReviewOutcome)}
                className="rounded-md border border-border bg-bg-card px-2 py-1.5 text-sm text-text-heading"
              >
                <option value="satisfactory">Satisfactory</option>
                <option value="concerns">Concerns</option>
                <option value="terminate">Terminate</option>
              </select>
              <input
                type="date"
                value={reviewedOn}
                onChange={(e) => setReviewedOn(e.target.value)}
                className="rounded-md border border-border bg-bg-card px-2 py-1.5 text-sm text-text-heading"
              />
              <button
                onClick={() => review.mutate()}
                disabled={!findings.trim() || review.isPending}
                className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                Record review
              </button>
            </div>
          </div>
        </Section>

        {!view.retired && (
          <button
            onClick={() => patch.mutate({ retiredOn: today() })}
            className="text-xs text-text-muted hover:text-danger"
          >
            Retire this vendor
          </button>
        )}
        {view.retired && (
          <p className="text-xs text-text-muted">Retired {v.retiredOn} — kept for history.</p>
        )}

        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
        {title}
      </h3>
      {children}
    </div>
  );
}
